import type React from "react";
import { NewsFeaturesSection } from "@/components/about/NewsFeaturesSection";
import { BlogNewsletterSignupSection } from "@/components/blog/BlogNewsletterSignupSection";
import { Footer } from "@/components/layout/Footer";
import { GlobalHeroBackground } from "@/components/layout/GlobalHeroBackground";
import { Navbar } from "@/components/layout/Navbar";
import { useSEO } from "@/hooks/useSEO";

const values = [
  {
    title: "Making Dance Accessible",
    description:
      "We believe dance should be within reach for everyone—regardless of schedule, experience, or location. Our tutorials are thoughtfully designed to meet you where you are and integrate seamlessly into your daily life.",
    Icon: AccessibilityValueIcon,
  },
  {
    title: "Empowering Confidence in Every Performer",
    description:
      "Whether you're preparing a solo or coordinating with a group, our structured tools and guided lessons are built to help you feel prepared, confident, and performance ready.",
    Icon: ConfidenceValueIcon,
  },
  {
    title: "Celebrating Without the Stress",
    description:
      "We're here to make sure your performance enhances your celebration, not add pressure to it. With BollyRhythm, you can focus on the moment, knowing the rest is taken care of.",
    Icon: CelebrationValueIcon,
  },
];

function StoryEyebrowIcon() {
  return (
    <svg
      width="100%"
      viewBox="0 0 19 19"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        opacity="0.2"
        d="M14.1981 11.1472L10.3246 12.5746L8.89724 16.4481C8.85782 16.5549 8.78661 16.6471 8.6932 16.7122C8.59979 16.7772 8.48867 16.8121 8.37482 16.8121C8.26097 16.8121 8.14986 16.7772 8.05645 16.7122C7.96304 16.6471 7.89182 16.5549 7.8524 16.4481L6.42506 12.5746L2.55154 11.1472C2.44473 11.1078 2.35258 11.0366 2.28749 10.9432C2.2224 10.8498 2.1875 10.7387 2.1875 10.6248C2.1875 10.511 2.2224 10.3999 2.28749 10.3064C2.35258 10.213 2.44473 10.1418 2.55154 10.1024L6.42506 8.67506L7.8524 4.80154C7.89182 4.69473 7.96304 4.60258 8.05645 4.53749C8.14986 4.4724 8.26097 4.4375 8.37482 4.4375C8.48867 4.4375 8.59979 4.4724 8.6932 4.53749C8.78661 4.60258 8.85782 4.69473 8.89724 4.80154L10.3246 8.67506L14.1981 10.1024C14.3049 10.1418 14.3971 10.213 14.4622 10.3064C14.5272 10.3999 14.5621 10.511 14.5621 10.6248C14.5621 10.7387 14.5272 10.8498 14.4622 10.9432C14.3971 11.0366 14.3049 11.1078 14.1981 11.1472Z"
        fill="currentColor"
      />
      <path
        d="M14.3952 9.57453L10.7685 8.23437L9.43255 4.60484C9.35343 4.38992 9.2103 4.20443 9.02245 4.07341C8.83461 3.94239 8.6111 3.87214 8.38208 3.87214C8.15305 3.87214 7.92954 3.94239 7.7417 4.07341C7.55386 4.20443 7.41072 4.38992 7.33161 4.60484L5.98723 8.23437L2.3577 9.57031C2.14278 9.64942 1.95729 9.79256 1.82627 9.9804C1.69525 10.1682 1.625 10.3918 1.625 10.6208C1.625 10.8498 1.69525 11.0733 1.82627 11.2612C1.95729 11.449 2.14278 11.5921 2.3577 11.6712L5.98723 13.0156L7.32317 16.6452C7.40228 16.8601 7.54542 17.0456 7.73326 17.1766C7.9211 17.3076 8.14462 17.3779 8.37364 17.3779C8.60266 17.3779 8.82617 17.3076 9.01402 17.1766C9.20186 17.0456 9.34499 16.8601 9.42411 16.6452L10.7685 13.0156L14.398 11.6797C14.6129 11.6006 14.7984 11.4574 14.9294 11.2696C15.0605 11.0818 15.1307 10.8582 15.1307 10.6292C15.1307 10.4002 15.0605 10.1767 14.9294 9.98884C14.7984 9.801 14.6129 9.65786 14.398 9.57875L14.3952 9.57453ZM10.1357 12.0467C10.0593 12.0749 9.98998 12.1192 9.93244 12.1768C9.8749 12.2343 9.83053 12.3036 9.80239 12.38L8.37786 16.2395L6.95614 12.3828C6.92804 12.3057 6.8834 12.2356 6.82534 12.1775C6.76727 12.1195 6.6972 12.0748 6.62005 12.0467L2.76341 10.625L6.62005 9.20328C6.6972 9.17519 6.76727 9.13054 6.82534 9.07248C6.8834 9.01441 6.92804 8.94434 6.95614 8.86719L8.37786 5.01055L9.79958 8.86719C9.82772 8.94354 9.87209 9.01288 9.92963 9.07042C9.98717 9.12796 10.0565 9.17233 10.1329 9.20047L13.9923 10.625L10.1357 12.0467ZM10.6279 3.3125C10.6279 3.16332 10.6871 3.02024 10.7926 2.91475C10.8981 2.80926 11.0412 2.75 11.1904 2.75H12.3154V1.625C12.3154 1.47582 12.3746 1.33274 12.4801 1.22725C12.5856 1.12176 12.7287 1.0625 12.8779 1.0625C13.027 1.0625 13.1701 1.12176 13.2756 1.22725C13.3811 1.33274 13.4404 1.47582 13.4404 1.625V2.75H14.5654C14.7145 2.75 14.8576 2.80926 14.9631 2.91475C15.0686 3.02024 15.1279 3.16332 15.1279 3.3125C15.1279 3.46168 15.0686 3.60476 14.9631 3.71025C14.8576 3.81574 14.7145 3.875 14.5654 3.875H13.4404V5C13.4404 5.14918 13.3811 5.29226 13.2756 5.39775C13.1701 5.50324 13.027 5.5625 12.8779 5.5625C12.7287 5.5625 12.5856 5.50324 12.4801 5.39775C12.3746 5.29226 12.3154 5.14918 12.3154 5V3.875H11.1904C11.0412 3.875 10.8981 3.81574 10.7926 3.71025C10.6871 3.60476 10.6279 3.46168 10.6279 3.3125ZM17.9404 6.6875C17.9404 6.83668 17.8811 6.97976 17.7756 7.08525C17.6701 7.19074 17.527 7.25 17.3779 7.25H16.8154V7.8125C16.8154 7.96168 16.7561 8.10476 16.6506 8.21025C16.5451 8.31574 16.402 8.375 16.2529 8.375C16.1037 8.375 15.9606 8.31574 15.8551 8.21025C15.7496 8.10476 15.6904 7.96168 15.6904 7.8125V7.25H15.1279C14.9787 7.25 14.8356 7.19074 14.7301 7.08525C14.6246 6.97976 14.5654 6.83668 14.5654 6.6875C14.5654 6.53832 14.6246 6.39524 14.7301 6.28975C14.8356 6.18426 14.9787 6.125 15.1279 6.125H15.6904V5.5625C15.6904 5.41332 15.7496 5.27024 15.8551 5.16475C15.9606 5.05926 16.1037 5 16.2529 5C16.402 5 16.5451 5.05926 16.6506 5.16475C16.7561 5.27024 16.8154 5.41332 16.8154 5.5625V6.125H17.3779C17.527 6.125 17.6701 6.18426 17.7756 6.28975C17.8811 6.39524 17.9404 6.53832 17.9404 6.6875Z"
        fill="currentColor"
      />
    </svg>
  );
}

function AccessibilityValueIcon() {
  return (
    <svg
      width="100%"
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M28 9H26V8C26 7.20435 25.6839 6.44129 25.1213 5.87868C24.5587 5.31607 23.7956 5 23 5H5C4.20435 5 3.44129 5.31607 2.87868 5.87868C2.31607 6.44129 2 7.20435 2 8V20C2 20.7956 2.31607 21.5587 2.87868 22.1213C3.44129 22.6839 4.20435 23 5 23H19V24C19 24.7956 19.3161 25.5587 19.8787 26.1213C20.4413 26.6839 21.2044 27 22 27H28C28.7956 27 29.5587 26.6839 30.1213 26.1213C30.6839 25.5587 31 24.7956 31 24V12C31 11.2044 30.6839 10.4413 30.1213 9.87868C29.5587 9.31607 28.7956 9 28 9ZM29 24C29 24.2652 28.8946 24.5196 28.7071 24.7071C28.5196 24.8946 28.2652 25 28 25H22C21.7348 25 21.4804 24.8946 21.2929 24.7071C21.1054 24.5196 21 24.2652 21 24V12C21 11.7348 21.1054 11.4804 21.2929 11.2929C21.4804 11.1054 21.7348 11 22 11H28C28.2652 11 28.5196 11.1054 28.7071 11.2929C28.8946 11.4804 29 11.7348 29 12V24ZM17 26C17 26.2652 16.8946 26.5196 16.7071 26.7071C16.5196 26.8946 16.2652 27 16 27H11C10.7348 27 10.4804 26.8946 10.2929 26.7071C10.1054 26.5196 10 26.2652 10 26C10 25.7348 10.1054 25.4804 10.2929 25.2929C10.4804 25.1054 10.7348 25 11 25H16C16.2652 25 16.5196 25.1054 16.7071 25.2929C16.8946 25.4804 17 25.7348 17 26ZM27 14C27 14.2652 26.8946 14.5196 26.7071 14.7071C26.5196 14.8946 26.2652 15 26 15H24C23.7348 15 23.4804 14.8946 23.2929 14.7071C23.1054 14.5196 23 14.2652 23 14C23 13.7348 23.1054 13.4804 23.2929 13.2929C23.4804 13.1054 23.7348 13 24 13H26C26.2652 13 26.5196 13.1054 26.7071 13.2929C26.8946 13.4804 27 13.7348 27 14Z"
        fill="currentColor"
      />
    </svg>
  );
}

function ConfidenceValueIcon() {
  return (
    <svg
      width="100%"
      viewBox="0 0 32 33"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M30 7.5V25.5C30 25.7652 29.8946 26.0196 29.7071 26.2071C29.5196 26.3946 29.2652 26.5 29 26.5H20C19.2056 26.5 18.4437 26.815 17.8813 27.376C17.3189 27.937 17.002 28.6982 17 29.4925C17.004 29.6967 16.9446 29.8971 16.83 30.0661C16.7154 30.2351 16.5512 30.3644 16.36 30.4363C16.2082 30.4948 16.0445 30.5155 15.8829 30.4964C15.7214 30.4774 15.5669 30.4192 15.433 30.327C15.299 30.2348 15.1895 30.1112 15.1141 29.9671C15.0386 29.823 14.9995 29.6627 15 29.5C15 28.7044 14.6839 27.9413 14.1213 27.3787C13.5587 26.8161 12.7956 26.5 12 26.5H3C2.73478 26.5 2.48043 26.3946 2.29289 26.2071C2.10536 26.0196 2 25.7652 2 25.5V7.5C2 7.23478 2.10536 6.98043 2.29289 6.79289C2.48043 6.60536 2.73478 6.5 3 6.5H11C12.0609 6.5 13.0783 6.92143 13.8284 7.67157C14.5786 8.42172 15 9.43913 15 10.5V21.4662C14.9965 21.7243 15.0909 21.9741 15.2641 22.1655C15.4374 22.3568 15.6766 22.4754 15.9338 22.4975C16.0705 22.5066 16.2077 22.4874 16.3368 22.4413C16.4659 22.3951 16.5841 22.3229 16.6841 22.2291C16.7841 22.1353 16.8637 22.022 16.9181 21.8961C16.9724 21.7703 17.0003 21.6346 17 21.4975V10.5C17 9.43913 17.4214 8.42172 18.1716 7.67157C18.9217 6.92143 19.9391 6.5 21 6.5H29C29.2652 6.5 29.5196 6.60536 29.7071 6.79289C29.8946 6.98043 30 7.23478 30 7.5Z"
        fill="currentColor"
      />
    </svg>
  );
}

function CelebrationValueIcon() {
  return (
    <svg
      width="100%"
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M13.9373 6.57875C13.7025 6.3445 13.4124 6.17318 13.0938 6.0806C12.7753 5.98802 12.4386 5.97716 12.1147 6.04903C11.7909 6.12089 11.4904 6.27317 11.2409 6.49181C10.9914 6.71044 10.801 6.98839 10.6873 7.3L4.12607 25.3475C4.01829 25.6451 3.98347 25.9642 4.02454 26.2781C4.06562 26.5919 4.18139 26.8914 4.36213 27.1512C4.54288 27.4111 4.78332 27.6238 5.06328 27.7715C5.34324 27.9191 5.65455 27.9975 5.97107 28C6.20412 27.9984 6.43509 27.9561 6.65357 27.875L24.6998 21.3125C25.0115 21.1989 25.2896 21.0086 25.5084 20.7592C25.7271 20.5098 25.8796 20.2093 25.9516 19.8855C26.0235 19.5616 26.0128 19.2248 25.9203 18.9062C25.8278 18.5876 25.6565 18.2975 25.4223 18.0625L13.9373 6.57875ZM8.14357 20.1413L10.5436 13.5425L18.4586 21.4575L11.8586 23.8575L8.14357 20.1413ZM20.0011 9C20.0199 8.32396 20.1835 7.65983 20.4811 7.0525C21.1436 5.72875 22.3936 5 24.0011 5C24.8386 5 25.3761 4.71375 25.7073 4.09875C25.8817 3.75468 25.9818 3.37777 26.0011 2.9925C26.0021 2.72728 26.1084 2.47332 26.2966 2.28649C26.4849 2.09966 26.7396 1.99526 27.0048 1.99625C27.27 1.99724 27.524 2.10356 27.7108 2.29179C27.8977 2.48003 28.0021 2.73478 28.0011 3C28.0011 4.6075 26.9361 7 24.0011 7C23.1636 7 22.6261 7.28625 22.2948 7.90125C22.1204 8.24532 22.0203 8.62223 22.0011 9.0075C22.0006 9.13882 21.9742 9.26876 21.9235 9.3899C21.8728 9.51103 21.7987 9.621 21.7055 9.71351C21.6123 9.80602 21.5018 9.87926 21.3803 9.92906C21.2588 9.97886 21.1286 10.0042 20.9973 10.0037C20.866 10.0033 20.7361 9.9769 20.6149 9.92619C20.4938 9.87548 20.3838 9.80141 20.2913 9.70821C20.1988 9.615 20.1256 9.50448 20.0758 9.38297C20.026 9.26145 20.0006 9.13132 20.0011 9ZM17.0011 5V2C17.0011 1.73478 17.1064 1.48043 17.294 1.29289C17.4815 1.10536 17.7359 1 18.0011 1C18.2663 1 18.5206 1.10536 18.7082 1.29289C18.8957 1.48043 19.0011 1.73478 19.0011 2V5C19.0011 5.26522 18.8957 5.51957 18.7082 5.70711C18.5206 5.89464 18.2663 6 18.0011 6C17.7359 6 17.4815 5.89464 17.294 5.70711C17.1064 5.51957 17.0011 5.26522 17.0011 5ZM29.7086 15.2925C29.8014 15.3854 29.875 15.4957 29.9252 15.6171C29.9754 15.7384 30.0012 15.8685 30.0012 15.9998C30.0011 16.1312 29.9752 16.2612 29.9249 16.3825C29.8746 16.5038 29.8009 16.614 29.7079 16.7069C29.615 16.7997 29.5047 16.8733 29.3834 16.9235C29.262 16.9737 29.132 16.9995 29.0006 16.9995C28.8693 16.9994 28.7393 16.9735 28.6179 16.9232C28.4966 16.8729 28.3864 16.7992 28.2936 16.7062L26.2936 14.7063C26.1059 14.5186 26.0005 14.2641 26.0005 13.9987C26.0005 13.7334 26.1059 13.4789 26.2936 13.2913C26.4812 13.1036 26.7357 12.9982 27.0011 12.9982C27.2664 12.9982 27.5209 13.1036 27.7086 13.2913L29.7086 15.2925ZM30.3173 9.94875L27.3173 10.9487C27.0657 11.0326 26.7911 11.0131 26.5538 10.8945C26.3166 10.7759 26.1362 10.5679 26.0523 10.3162C25.9684 10.0646 25.988 9.78999 26.1066 9.55276C26.2252 9.31552 26.4332 9.13513 26.6848 9.05125L29.6848 8.05125C29.9364 7.96738 30.2111 7.98689 30.4483 8.10551C30.6855 8.22413 30.8659 8.43213 30.9498 8.68375C31.0337 8.93537 31.0142 9.21001 30.8956 9.44724C30.7769 9.68448 30.5689 9.86487 30.3173 9.94875Z"
        fill="currentColor"
      />
    </svg>
  );
}

function StoryTagHeartIcon() {
  return (
    <svg
      width="100%"
      viewBox="0 0 32 33"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M30 13.512C30 22.262 17.0262 29.3445 16.4737 29.637C16.3281 29.7153 16.1654 29.7563 16 29.7563C15.8346 29.7563 15.6719 29.7153 15.5262 29.637C14.9738 29.3445 2 22.262 2 13.512C2.00232 11.4572 2.81958 9.48735 4.27248 8.03444C5.72539 6.58154 7.69528 5.76428 9.75 5.76196C12.3313 5.76196 14.5912 6.87196 16 8.74821C17.4088 6.87196 19.6688 5.76196 22.25 5.76196C24.3047 5.76428 26.2746 6.58154 27.7275 8.03444C29.1804 9.48735 29.9977 11.4572 30 13.512Z"
        fill="currentColor"
      />
    </svg>
  );
}

function EyebrowBadge({
  color,
  children,
}: {
  color: "pink" | "yellow";
  children: React.ReactNode;
}) {
  const textColor = color === "yellow" ? "text-yellow-brand" : "text-pink-dark";
  return (
    <div
      className={`flex w-fit mx-auto p-[0.38rem] border border-[rgb(118_42_92/10%)] rounded-full bg-[color-mix(in_srgb,#ffffff_40%,transparent)] ${textColor} font-semibold`}
    >
      <div className="flex items-center gap-1 px-[0.88rem] py-2 border border-inherit rounded-[inherit] bg-white">
        <div className="w-5 flex flex-col [&>svg]:w-full [&>svg]:h-auto [&>svg]:block">
          <StoryEyebrowIcon />
        </div>
        <span className="m-0 text-[0.88rem] font-semibold leading-none text-inherit">
          {children}
        </span>
      </div>
    </div>
  );
}

function IconWrap({
  color,
  children,
}: {
  color: "pink" | "yellow";
  children: React.ReactNode;
}) {
  const colorClass =
    color === "yellow"
      ? "text-yellow-brand bg-[color-mix(in_srgb,var(--color-yellow-brand)_20%,transparent)]"
      : "text-pink-dark bg-[color-mix(in_srgb,var(--color-pink-dark)_25%,transparent)]";
  return (
    <div
      className={`w-[clamp(2.5rem,2.2143rem+1.4286vw,3.5rem)] min-w-[clamp(2.5rem,2.2143rem+1.4286vw,3.5rem)] min-h-[clamp(2.5rem,2.2143rem+1.4286vw,3.5rem)] flex flex-none items-center justify-center aspect-square rounded-full ${colorClass}`}
    >
      <div className="w-[clamp(1.75rem,1.6786rem+0.3571vw,2rem)] flex [&>svg]:w-full [&>svg]:h-auto [&>svg]:block">
        {children}
      </div>
    </div>
  );
}

export function AboutUsPage() {
  useSEO({
    title: "About Us",
    description:
      "Choreographed Bollywood tutorials for all skill levels--perfect for South Asian events. Learn and create performances anytime, anywhere, and arrive confident.",
    canonical: "/about-us",
  });

  return (
    <div className="page-wrapper landing-page relative">
      <Navbar />
      <GlobalHeroBackground />

      <main className="landing-page__main main-wrapper">
        {/* ── Hero Section ── */}
        <section>
          <div className="padding-global">
            <div className="container-large">
              <div className="py-20 max-[991px]:py-16 max-[767px]:py-12">
                <div className="flex flex-col gap-16">
                  <div className="text-align-center">
                    <EyebrowBadge color="pink">Our Story</EyebrowBadge>
                    <div className="spacer-medium" />
                    <h1 className="heading-style-h2 align-center max-w-[22ch]">
                      Hi, I’m Khusbu, The Founder And Owner Of BollyRhythm.
                    </h1>
                  </div>

                  <div
                    className="relative flex items-center justify-center gap-12 mb-12 max-[767px]:gap-8 max-[767px]:mb-4 max-[479px]:flex-col max-[479px]:gap-6"
                    aria-hidden="true"
                  >
                    <div className="w-full max-w-[28rem] aspect-[4.4/6] rounded-[1.88rem] bg-center bg-no-repeat bg-cover shadow-[inset_0_0_0_12px_var(--color-yellow-brand),inset_0_0_0_24px_#ffffff] origin-top-right -rotate-[8deg] bg-pink-darker bg-[image:url('https://cdn.prod.website-files.com/686229256a807206c1874545/68679f7b9e1d7ab31559839b_About%20Image%202.avif')] max-[991px]:rounded-[1.5rem] max-[991px]:shadow-[inset_0_0_0_8px_var(--color-yellow-brand),inset_0_0_0_16px_#ffffff] max-[767px]:rounded-[1rem] max-[767px]:shadow-[inset_0_0_0_6px_var(--color-yellow-brand),inset_0_0_0_12px_#ffffff] max-[479px]:aspect-[4.5/7] max-[479px]:rotate-0 max-[479px]:max-w-[20rem]" />
                    <div className="w-full max-w-[28rem] aspect-[4.4/6] rounded-[1.88rem] bg-center bg-no-repeat bg-cover shadow-[inset_0_0_0_12px_var(--color-yellow-brand),inset_0_0_0_24px_#ffffff] origin-top-left rotate-[8deg] bg-white bg-[image:url('https://cdn.prod.website-files.com/686229256a807206c1874545/68679f8a8310a83541d93752_About%20Image.avif')] max-[991px]:rounded-[1.5rem] max-[991px]:shadow-[inset_0_0_0_8px_var(--color-yellow-brand),inset_0_0_0_16px_#ffffff] max-[767px]:rounded-[1rem] max-[767px]:shadow-[inset_0_0_0_6px_var(--color-yellow-brand),inset_0_0_0_12px_#ffffff] max-[479px]:aspect-[4.5/7] max-[479px]:rotate-0 max-[479px]:max-w-[20rem]" />
                    <div className="absolute top-0 -z-[2] h-72 aspect-square rounded-full bg-[#f2a435] blur-[220px]" />
                  </div>

                  <div className="text-align-center max-w-[70rem] mx-auto">
                    <p className="m-0 text-[#4a4a4a] text-[clamp(1rem,0.9643rem+0.1786vw,1.125rem)] font-medium leading-[1.5]">
                      I've been dancing for as long as I can remember and have
                      been teaching for over 15 years. What truly inspires me is
                      witnessing the joy on my students&apos; faces when they
                      finally learn and perform a dance. Sharing something that
                      brings me so much happiness and connects me to my culture
                      is incredibly fulfilling.
                      <br />
                      <br />
                      I&apos;ve observed how the demands of modern life,{" "}
                      <span className="text-[#762a5c]">
                        with family and friends often spread out, can make
                        attending weekly in-person classes and rehearsals
                        stressful and overwhelming.
                      </span>{" "}
                      This pressure, especially with tight deadlines for event
                      performances, made me realize there had to be a better way
                      to experience the joy of dance.
                      <br />
                      <br />I love dance too much to see it become a source of
                      stress or anxiety.{" "}
                      <span className="text-[#762a5c]">
                        That&apos;s why I created BollyRhythm—to make dance
                        accessible to everyone, fitting seamlessly into your
                        schedule and lifestyle.
                      </span>{" "}
                      With BollyRhythm, you can experience the joy, excitement,
                      and sense of community that comes with being part of a
                      performance, all without the usual stress.{" "}
                      <span className="text-[#762a5c]">
                        It&apos;s about celebrating culture, creating lasting
                        memories, and bringing people together in a way that
                        truly works for you.
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Values Section ── */}
        <section>
          <div className="padding-global">
            <div className="container-large">
              <div className="text-center pt-16 max-[767px]:pt-12">
                <h2 className="heading-style-h2 text-align-center align-center max-w-[13ch]">
                  Values That{" "}
                  <span className="text-[#762a5c]">Shape Our Studio</span>
                </h2>

                <div className="grid grid-cols-[repeat(3,minmax(0,1fr))] gap-6 mt-10 max-[991px]:grid-cols-[repeat(2,minmax(0,1fr))] max-[767px]:flex max-[767px]:flex-col">
                  {values.map(({ title, description, Icon }) => (
                    <div
                      key={title}
                      className="rounded-[1.25rem] bg-white p-6 text-left"
                    >
                      <IconWrap color="yellow">
                        <Icon />
                      </IconWrap>
                      <div className="spacer-medium" />
                      <h3 className="m-0 text-[#1f0a18] heading-style-h4">
                        {title}
                      </h3>
                      <div className="spacer-xsmall" />
                      <p className="m-0 text-[#4a4a4a] text-[clamp(1rem,0.9643rem+0.1786vw,1.125rem)] font-medium leading-[1.5]">
                        {description}
                      </p>
                    </div>
                  ))}
                </div>

                <div className="flex flex-col items-center justify-center mt-10 max-[479px]:mt-20 max-[479px]:mb-6">
                  <IconWrap color="pink">
                    <StoryTagHeartIcon />
                  </IconWrap>
                  <div className="spacer-small" />
                  <div className="heading-style-h4 text-center">
                    Our Moves.
                    <br />
                    <span className="text-[#762a5c]">Your Dance.</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <NewsFeaturesSection />

        <BlogNewsletterSignupSection />
      </main>

      <Footer />
    </div>
  );
}

export default AboutUsPage;
